/**
 * scheduleScenario.ts
 * Frontend service for the Scenario API (FEAT-2026-0007, Phase 6B.2).
 *
 * All requests go through apiClient (MSAL auth + error capture).
 * No raw fetch() calls — see LESSONS-LEARNED.md for why direct fetch 404s in dev.
 *
 * Endpoints (mounted at /api/scenarios):
 *   POST   /api/scenarios                 — create Draft for source
 *   GET    /api/scenarios/active          — active Draft/NeedsReview for source (?source=&sourceKey=)
 *   GET    /api/scenarios/:key            — get by key
 *   PATCH  /api/scenarios/:key/status     — advance/revert status
 *   DELETE /api/scenarios/:key            — soft delete
 *   POST   /api/scenarios/:key/duplicate  — clone bookings/assignments under a new ScenarioKey
 */

import apiClient from './apiClient';

// ---------------------------------------------------------------------------
// Types — mirror backend/src/models/scenarioModels.ts
// ---------------------------------------------------------------------------

export type ScenarioStatus = 'Draft' | 'NeedsReview' | 'Submitted' | 'Archived';
export type ScenarioSourceType = 'Quote' | 'WorkOrder';

/** Frontend representation of a Scenario row (backend + audit fields). */
export interface Scenario {
  ScenarioKey: string;
  /** Display ID: 'SP-000' (BASELINE), 'SP-001', 'SP-002', … */
  Code: string;
  /** Human-readable plan name. */
  Name: string;
  /** Lifecycle status. BASELINE is always 'Submitted'. */
  Status: ScenarioStatus;
  /** Which source system this scenario is planning for. Null for BASELINE. */
  SourceType: ScenarioSourceType | null;
  /** FK → Quote.QuoteKey. Set when SourceType='Quote'. */
  QuoteKey: string | null;
  /** FK → WorkOrder.WorkOrderKey. Set when SourceType='WorkOrder'. */
  WorkOrderKey: string | null;
  // ── Source identity (2026-07-10 scenario-naming fix) ─────────────────────
  // Single-row reads (GET /scenarios/:key, /active, /for-source) now JOIN the
  // source's display identity (backend scenarioModels.ScenarioWithSource).
  // Optional so pre-fix payloads / stale backends don't break callers.
  /** Quote.QuoteNumber (e.g. 'Q-2026-9065'). SourceType='Quote' only. */
  QuoteNumber?: string | null;
  /** Quote.Title (e.g. 'Terrane'). SourceType='Quote' only. */
  QuoteTitle?: string | null;
  /** Client name via the quote's client. SourceType='Quote' only. */
  ClientName?: string | null;
  /** WorkOrder.WorkOrderId (e.g. 'WO-2026-045'). SourceType='WorkOrder' only. */
  WorkOrderId?: string | null;
  /** WorkOrder descriptive name. SourceType='WorkOrder' only. */
  WorkOrderName?: string | null;
  /** Project name via the work order's project. SourceType='WorkOrder' only. */
  ProjectName?: string | null;
  // Audit fields
  sysCreateUser: string | null;
  sysCreateDate: string | null;
  sysLastUser: string | null;
  sysLastDate: string | null;
  sysLastAction: string | null;
}

/** Payload for creating a new Draft scenario from a source. */
export interface CreateScenarioDraftPayload {
  SourceType: ScenarioSourceType;
  /** Key of the quote or work order to plan. */
  SourceKey: string;
  /** Optional human-readable name. Defaults to 'Draft <Code>' when omitted. */
  name?: string;
}

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

/**
 * Creates a new Draft scenario from a quote or work order.
 * Maps to: POST /api/scenarios
 */
export async function createScenarioDraft(
  payload: CreateScenarioDraftPayload,
): Promise<Scenario> {
  try {
    const response = await apiClient.post<Scenario>('/scenarios', {
      SourceType: payload.SourceType,
      SourceKey: payload.SourceKey,
      Name: payload.name,
    });
    return response.data;
  } catch (err: unknown) {
    // Surface the backend's helpful message (e.g. "No work order matches 'X'.")
    // instead of the generic "Request failed with status code 400/500".
    const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
    throw new Error(msg ?? (err instanceof Error ? err.message : 'Failed to create scenario.'));
  }
}

/**
 * Fetches a single Scenario by its ScenarioKey.
 * Maps to: GET /api/scenarios/:key
 */
export async function getScenario(key: string): Promise<Scenario> {
  const response = await apiClient.get<Scenario>(`/scenarios/${key}`);
  return response.data;
}

/**
 * Finds the current non-deleted Draft or NeedsReview scenario for the given
 * source type + key. Returns null (404 → null) when none exists.
 * Maps to: GET /api/scenarios/active?source=&sourceKey=
 */
export async function getActiveScenarioForSource(
  source: ScenarioSourceType,
  sourceKey: string,
): Promise<Scenario | null> {
  try {
    const response = await apiClient.get<Scenario>('/scenarios/active', {
      params: { source, sourceKey },
    });
    return response.data;
  } catch (err: unknown) {
    // 404 = no active draft exists; anything else re-throw
    if (
      err &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 404
    ) {
      return null;
    }
    throw err;
  }
}

/**
 * Advances or reverts a scenario's status.
 * Maps to: PATCH /api/scenarios/:key/status
 * Allowed transitions: Draft→NeedsReview→Submitted; any→Archived.
 */
export async function updateScenarioStatus(
  key: string,
  status: ScenarioStatus,
): Promise<Scenario> {
  const response = await apiClient.patch<Scenario>(`/scenarios/${key}/status`, {
    Status: status,
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// Delete Draft cascade (FEAT-2026-0007 Phase 6F.2)
// ---------------------------------------------------------------------------

/** Response from DELETE /api/scenarios/:key */
export interface DeleteScenarioResult {
  bookingsDeleted:    number;
  assignmentsDeleted: number;
  /** Index Tests 4.3: pooled IndexAllocation rows cascaded with this draft. */
  indexAllocationsDeleted?: number;
}

/**
 * Soft-deletes a draft scenario and all its rows (cascade).
 * Maps to: DELETE /api/scenarios/:key
 *
 * The backend guards:
 *   - BASELINE cannot be deleted (400).
 *   - Submitted scenarios cannot be deleted (409) — they represent committed plans.
 *   - Archived scenarios cannot be deleted (400).
 *
 * Returns the freed counts on success. Callers should surface these in a snackbar.
 */
export async function deleteScenario(key: string): Promise<DeleteScenarioResult> {
  const response = await apiClient.delete<DeleteScenarioResult>(`/scenarios/${key}`);
  return response.data;
}

// ---------------------------------------------------------------------------
// Submit Plan (FEAT-2026-0007 Phase 6D.1)
// ---------------------------------------------------------------------------

/** Response from POST /api/scenarios/:key/submit */
export interface SubmitScenarioResult {
  bookingsCommitted: number;
  assignmentsCommitted: number;
  /** Index Tests 4.3: pooled IndexAllocation rows flipped Tentative→Committed. */
  indexAllocationsCommitted?: number;
  status: ScenarioStatus;
  /**
   * Quote-sourced plans only (2026-08-06 "finalize = won"): finalizing also
   * auto-accepts the quote and converts it into a Work Order. Success carries
   * the new WO's identifiers (navigate there); `error` reports a conversion
   * failure (the plan submit itself still succeeded); `alreadyConverted`
   * covers a replayed finalize.
   */
  conversion?: {
    workOrderKey?: string;
    workOrderId?: string;
    projectKey?: string;
    projectId?: string;
    unmappedLines?: string[];
    placeholderTests?: string[];
    alreadyConverted?: boolean;
    error?: string;
  };
}

/**
 * Submits a scenario: atomically flips all its Tentative bookings and
 * assignments to Committed (ScenarioKey kept for provenance), then marks
 * the Scenario itself as Submitted.
 *
 * The backend returns 409 if already Submitted, 400 for BASELINE/Archived,
 * 404 if not found.  Callers should handle those with a try/catch.
 *
 * Maps to: POST /api/scenarios/:key/submit
 */
export async function submitScenario(key: string): Promise<SubmitScenarioResult> {
  const response = await apiClient.post<SubmitScenarioResult>(`/scenarios/${key}/submit`);
  return response.data;
}

/** Response from POST /api/scenarios/:key/reopen */
export interface ReopenScenarioResult {
  bookingsReopened: number;
  assignmentsReopened: number;
  /** Index Tests 4.3: pooled IndexAllocation rows flipped back Committed→Tentative. */
  indexAllocationsReopened?: number;
  status: ScenarioStatus;
}

/**
 * Reopens a scheduled (Submitted) scenario so it can be edited again — the inverse of
 * submit. Un-commits this plan's rows back to Tentative and sets Status='Draft'.
 *
 * The backend returns 400 for BASELINE / not-Submitted, 404 if not found.
 *
 * Maps to: POST /api/scenarios/:key/reopen
 */
export async function reopenScenario(key: string): Promise<ReopenScenarioResult> {
  const response = await apiClient.post<ReopenScenarioResult>(`/scenarios/${key}/reopen`);
  return response.data;
}

// ---------------------------------------------------------------------------
// Pre-submit safety check (FEAT-2026-0007 — read-only dry run)
// ---------------------------------------------------------------------------

/** A task with a machine booking in this plan but no employee assigned. */
export interface UnassignedTaskSummary {
  taskKey: string;
  taskName: string;
}

/**
 * Wave 3 (Q3B): a (user, week) where a task lands on a day the person has a
 * dated absence — the one remaining HARD conflict. Legacy name/shape kept
 * (this used to be the per-day >100% sum check, retired by the binary model).
 */
export interface OverbookedWeekSummary {
  userKey: string;
  displayName: string;
  weekStart: string;
  /** Legacy-scale load on the conflict day (task + absence = 200). */
  maxDayLoad: number;
  /** Wave-3 additive: the conflicting days ('YYYY-MM-DD', ascending). */
  conflictDays?: string[];
}

/**
 * NON-blocking week-overload advisory. 2026-08-05: rebased onto the allocation
 * truth — Σ Staffing-Allocation % across DISTINCT sources for the week
 * (count-once-per-source, presence-fallback 100), i.e. the SAME number the
 * Team-capacity grid renders. >100 warns, never blocks. (The old Σ task-days
 * basis false-alarmed on routine parallel lab work — presence rows carry no
 * intensity.) Mirror of backend scenarioSubmitCheckService.WeekOverloadSummary.
 */
export interface WeekOverloadSummary {
  userKey: string;
  displayName: string;
  weekStart: string;
  /** Σ allocation % across sources for the week (count-once-per-source). */
  allocationPercent: number;
}

/** A hard machine-day conflict in the resulting (committed + draft) schedule. */
export interface HardConflictSummary {
  machineCode: string;
  machineName: string;
  overlapStart: string;
  overlapEnd: string;
  taskNames: string[];
}

/** A draft scenario holding a person this plan staffs (attribution for a soft hold). */
export interface CrossScenarioHoldSource {
  scenarioKey: string;
  code: string | null;
  name: string | null;
  owner: string | null;
}

/** A person this plan staffs who is also held (tentatively) in other draft plans. */
export interface CrossScenarioHoldSummary {
  userKey: string;
  displayName: string;
  sources: CrossScenarioHoldSource[];
  /**
   * P2-6 (audit #2): working days in the overlap held by another plan —
   * presence language (the retired stored-percent `maxPercent` is gone).
   */
  heldDays: number;
}

/** Response from GET /api/scenarios/:key/submit-check */
export interface SubmitCheckResult {
  unassignedTasks: UnassignedTaskSummary[];
  overbookedWeeks: OverbookedWeekSummary[];
  hardConflicts: HardConflictSummary[];
  /** People this plan staffs who are also held in OTHER draft plans (soft, advisory). */
  crossScenarioHolds: CrossScenarioHoldSummary[];
  /**
   * G-4 (audit 2026-07-01): source test names with NO catalog mapping — missing
   * from the plan entirely. Optional so a stale backend during rollout doesn't
   * break the dialog (BE↔FE payloads are hand-duplicated — mirror the backend
   * scenarioSubmitCheckService.SubmitCheckResult field EXACTLY).
   */
  unmappedTests?: string[];
  /**
   * Wave 3 (R2): weeks where a person carries more task-days than the week
   * holds. Non-blocking advisory bullet; the SERVER folds it into `clean`.
   * Optional so a stale backend during rollout doesn't break the dialog.
   */
  weekOverloaded?: WeekOverloadSummary[];
  /**
   * Index Tests 4.3: count of this scenario's active Tentative IndexAllocation
   * rows — informational, never affects `clean`. Optional so a stale backend
   * during rollout doesn't break the dialog.
   */
  indexAllocationCount?: number;
  /** True when every list (incl. weekOverloaded, since R2) is empty. */
  clean: boolean;
}

/**
 * Read-only pre-submit check. Returns the problems that submitting this scenario
 * would carry into the live schedule so the Submit button can warn-and-confirm.
 * Never throws on plan content — only on transport/auth errors.
 *
 * Maps to: GET /api/scenarios/:key/submit-check
 */
export async function checkScenarioSubmit(key: string): Promise<SubmitCheckResult> {
  const response = await apiClient.get<SubmitCheckResult>(`/scenarios/${key}/submit-check`);
  return response.data;
}

// ---------------------------------------------------------------------------
// Duplicate Scenario (FEAT-2026-0007 Phase 6F.1)
// ---------------------------------------------------------------------------

/** Response from POST /api/scenarios/:key/duplicate */
export interface DuplicateScenarioResult {
  scenario: Scenario;
  bookingsCloned: number;
  assignmentsCloned: number;
  /** Index Tests 4.3: pooled IndexAllocation rows cloned under the new ScenarioKey. */
  indexAllocationsCloned?: number;
}

/**
 * Duplicates a scenario: clones all its non-deleted MachineBookings and
 * EmployeeAssignments under a new ScenarioKey (Status='Draft').
 * Cloned rows are set to Status='Tentative'.
 *
 * Returns the new Scenario plus clone counts.
 * The backend returns 400 if BASELINE is passed, 404 if source not found.
 *
 * Maps to: POST /api/scenarios/:key/duplicate
 */
export async function duplicateScenario(key: string): Promise<DuplicateScenarioResult> {
  const response = await apiClient.post<DuplicateScenarioResult>(`/scenarios/${key}/duplicate`);
  return response.data;
}

// ---------------------------------------------------------------------------
// List Scenarios (FEAT-2026-0007 Phase 6F.5)
// ---------------------------------------------------------------------------

/**
 * A single row in the scenario list dashboard.
 * BASELINE is excluded from this list by the backend.
 *
 * 2026-07-10 scenario-naming fix: the backend list additively JOINs each
 * row's SOURCE identity (Quote → number/title/client; WorkOrder →
 * id/name/project) so the dashboard can show what a plan is for. The source
 * fields are optional so a stale backend during rollout doesn't break the
 * list (hand-mirrored from backend scenarioModels.ScenarioListRow).
 */
export interface ScenarioListItem {
  ScenarioKey: string;
  Code: string;
  Name: string;
  Status: ScenarioStatus;
  SourceType: ScenarioSourceType | null;
  QuoteKey: string | null;
  WorkOrderKey: string | null;
  /** Quote.QuoteNumber (e.g. 'Q-2026-9065'). SourceType='Quote' only. */
  QuoteNumber?: string | null;
  /** Quote.Title (e.g. 'Terrane'). SourceType='Quote' only. */
  QuoteTitle?: string | null;
  /** Client name via the quote's client. SourceType='Quote' only. */
  ClientName?: string | null;
  /** WorkOrder.WorkOrderId (e.g. 'WO-2026-045'). SourceType='WorkOrder' only. */
  WorkOrderId?: string | null;
  /** WorkOrder descriptive name. SourceType='WorkOrder' only. */
  WorkOrderName?: string | null;
  /** Project name via the work order's project. SourceType='WorkOrder' only. */
  ProjectName?: string | null;
  sysCreateDate: string | null;
  sysCreateUser: string | null;
  sysLastModifiedDate: string | null;
  sysLastModifiedUser: string | null;
  sysLastAction: string | null;
}

/**
 * Lists all non-BASELINE, non-deleted scenarios ordered by creation date (newest first).
 * Maps to: GET /api/scenarios
 */
export async function listScenarios(): Promise<ScenarioListItem[]> {
  const response = await apiClient.get<ScenarioListItem[]>('/scenarios');
  return response.data;
}

/**
 * Lists ALL non-deleted scenarios (any status, incl. Submitted/Archived) for
 * one source, newest first. Used by the resolve flow to detect a locked
 * (Submitted) plan before auto-creating a duplicate draft (2026-07-10
 * silent-duplicate fix). Returns [] when the source has no scenarios or the
 * source id matches nothing.
 *
 * Maps to: GET /api/scenarios/for-source?source=&sourceKey=
 */
export async function listScenariosForSource(
  source: ScenarioSourceType,
  sourceKey: string,
): Promise<Scenario[]> {
  const response = await apiClient.get<Scenario[]>('/scenarios/for-source', {
    params: { source, sourceKey },
  });
  return response.data;
}

// ---------------------------------------------------------------------------
// Source ↔ Schedule impact + sync (FEAT-2026-0012)
//
// Powers the persistent "schedule link" banner on the Quote / Work Order
// editors and the warn-before-delete cascade.  A scenario is "attached" to a
// source (Quote or WorkOrder) when it plans that source's tasks.  isFinalized
// = the scenario is live/committed on the master schedule, so deleting the
// source must release those committed bookings first.
// ---------------------------------------------------------------------------

/** How fully a scenario's tasks are scheduled. */
export type ScenarioPlannedness = 'empty' | 'partial' | 'full';

/** One scenario attached to a source, with planning + commit counts. */
export interface ScenarioImpact {
  scenarioKey: string;
  code: string;
  name: string;
  status: ScenarioStatus;
  /** True when the scenario is live/committed on the master schedule. */
  isFinalized: boolean;
  taskCount: number;
  plannedTaskCount: number;
  fullyPlannedTaskCount: number;
  committedBookings: number;
  tentativeBookings: number;
  plannedness: ScenarioPlannedness;
  /**
   * Item 4.2 (index tests): active pooled IndexAllocation rows for this source
   * within this scenario. Optional — older payloads (pre-4.2 backend) omit it.
   */
  indexAllocationCount?: number;
}

/** Full impact summary for a Quote or WorkOrder source. */
export interface SourceScheduleImpact {
  sourceType: ScenarioSourceType;
  sourceKey: string;
  /** True when at least one scenario is attached to this source. */
  hasScenario: boolean;
  /** True when at least one attached scenario is finalized (live/committed). */
  hasFinalized: boolean;
  /**
   * P2-9: active COMMITTED bookings on the source's tasks across ALL scenarios
   * (incl. BASELINE) — independent of Scenario-row existence. Optional so
   * older payloads (pre-P2-9 backend) don't break the banner.
   * Hand-mirrored from backend scenarioSourceSyncService.SourceScheduleImpact.
   */
  sourceCommittedBookings?: number;
  scenarios: ScenarioImpact[];
  totals: {
    taskCount: number;
    plannedTaskCount: number;
    fullyPlannedTaskCount: number;
    committedBookings: number;
    tentativeBookings: number;
    /** Item 4.2: Σ of scenarios[].indexAllocationCount. Optional — older payloads omit it. */
    indexAllocationCount?: number;
  };
  /** Drift between the source's current line items and the planned tasks. */
  stale: {
    addedLineItems: number;
    removedTasks: number;
    /**
     * P-8b: line items whose test type was remapped in place, or (W2-2, quotes
     * only) whose quantity changed since the last sync (optional — older
     * payloads omit it).
     */
    changedLineItems?: number;
    /**
     * G2-11: booked tasks whose persisted per-test machine rate differs from
     * the current taxonomy rate (an admin edited MachineTaskCapability
     * minutes). Sync refreshes the rate. Optional — older payloads omit it.
     */
    rateDriftTasks?: number;
    /**
     * RM-16: booked tasks whose RunMode snapshot differs from the live
     * TaskType.RunMode (an admin flipped the type's run mode — attended vs
     * continuous 24/7). Sync adopts the flip. Optional — older payloads omit it.
     */
    runModeDriftTasks?: number;
    isStale: boolean;
  };
}

/** Result of a source-sync run (treated loosely — fields may be absent). */
export interface SourceSyncResult {
  tasksAdded?: number;
  tasksUpdated?: number;
  tasksRemoved: number;
  /** P-20a: bookings released by the sync prune (all scenarios). */
  bookingsRemoved?: number;
  /** P-20a: staffing rows released by the sync prune. */
  assignmentsRemoved?: number;
  /** P-20a: how many released bookings were Committed (live schedule). */
  committedBookingsRemoved?: number;
  /** W2-3 (P-8c): tentative bookings resized to the current qty/duration. */
  bookingsRespanned?: number;
  /** P2-11: bookings whose stale STAFFING was re-fanned to the booking span (self-heal). */
  staffingHealed?: number;
  /** W2-3 (P-8c): tasks whose COMMITTED bookings still reflect old values (needs manual follow-up). */
  committedDriftTasks?: string[];
  /** Item 4.2 (I19): IndexAllocation rows soft-deleted because their TaskType's pooled qty fell to zero. */
  indexAllocationsRemoved?: number;
}

/**
 * Fetches the schedule-impact summary for a quote or work order: which
 * scenarios are attached, how fully planned they are, whether any is finalized
 * (live), and whether the plan has drifted from the source's line items.
 *
 * Maps to: GET /api/scenarios/source-impact?source=&sourceKey=
 */
export async function getSourceScheduleImpact(
  source: ScenarioSourceType,
  sourceKey: string,
): Promise<SourceScheduleImpact> {
  const response = await apiClient.get<SourceScheduleImpact>('/scenarios/source-impact', {
    params: { source, sourceKey },
  });
  return response.data;
}

/**
 * Re-syncs the attached scenario's tasks with the source's current line items:
 * adds tasks for new line items, updates changed ones, removes tasks whose
 * source line item is gone.  Returns the change counts for a snackbar.
 *
 * P-5: the backend targets the source's attached scenario by default (the same
 * "primary" scenario the banner shows). `scenarioKey` is an optional explicit
 * override for future use — the banner does not pass it.
 *
 * W2-4 (P-20b): when the sync would release COMMITTED bookings and
 * `opts.confirmRelease` is not set, the backend refuses with 409
 * { code: 'SYNC_RELEASES_COMMITTED', committedBookings, taskNames } — the
 * useSourceScheduleImpact hook turns that into a blocked result so callers can
 * confirm ("Release & sync") and retry with the flag.
 *
 * Maps to: POST /api/scenarios/source-sync
 */
export async function syncSourceSchedule(
  source: ScenarioSourceType,
  sourceKey: string,
  scenarioKey?: string,
  opts?: { confirmRelease?: boolean },
): Promise<SourceSyncResult> {
  const response = await apiClient.post<SourceSyncResult>('/scenarios/source-sync', {
    source,
    sourceKey,
    ...(scenarioKey ? { scenarioKey } : {}),
    ...(opts?.confirmRelease ? { confirmRelease: true } : {}),
  });
  return response.data;
}
